/**
 * Orden del listado en vistas compartidas (lista común y CAD).
 * Misma UX; cada dominio resuelve precio/superficie de su shape.
 */
import { resolveLotAreaM2 } from './surfaceDisplay';

export const DEFAULT_LIST_SORT = 'name';

export const LIST_SORT_OPTIONS = [
  { value: 'name', label: 'Nombre' },
  { value: 'price-desc', label: 'Precio: mayor a menor' },
  { value: 'price-asc', label: 'Precio: menor a mayor' },
  { value: 'area-desc', label: 'Superficie: mayor a menor' },
  { value: 'area-asc', label: 'Superficie: menor a mayor' },
];

/** Dueño / editor: ordenan por consultas del lote. El visitante no las ve. */
export const INQUIRY_SORT_OPTIONS = [
  { value: 'inquiries-desc', label: 'Más consultas' },
  { value: 'inquiries-asc', label: 'Menos consultas' },
];

export const MANAGE_LIST_SORT_OPTIONS = [
  ...LIST_SORT_OPTIONS,
  ...INQUIRY_SORT_OPTIONS,
];

/**
 * @param {string} sortBy
 * @returns {string}
 */
export const getListSortLabel = (sortBy) => (
  MANAGE_LIST_SORT_OPTIONS.find((option) => option.value === sortBy)?.label || 'Nombre'
);

const toNumber = (value) => {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

const compareNames = (a, b) => String(a || '').localeCompare(String(b || ''), 'es', {
  sensitivity: 'base',
  numeric: true,
});

/**
 * Compara dos valores opcionales. Nulls al final. Empate → 0.
 * @param {number|null} a
 * @param {number|null} b
 * @param {1|-1} dir
 */
export const compareOptionalNumbers = (a, b, dir) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return dir * (a - b);
};

/**
 * @param {string} sortBy
 * @returns {1|-1}
 */
export const listSortDirection = (sortBy) => (
  sortBy === 'price-desc' || sortBy === 'area-desc' || sortBy === 'inquiries-desc' ? -1 : 1
);

export const isInquirySort = (sortBy) => (
  sortBy === 'inquiries-desc' || sortBy === 'inquiries-asc'
);

/**
 * Precios publicados del lote (UFs + contenido de lote + raíz).
 * @param {object|null|undefined} lot
 * @returns {number[]}
 */
export const collectSharedLotPrices = (lot) => {
  const prices = [];
  const push = (value) => {
    const n = toNumber(value);
    if (n != null && n > 0) prices.push(n);
  };
  (Array.isArray(lot?.units) ? lot.units : []).forEach((unit) => push(unit?.price));
  push(lot?.lot_user_content?.price);
  push(lot?.price);
  return prices;
};

/**
 * Precio representativo para ordenar: mínimo en asc, máximo en desc.
 * @param {object|null|undefined} lot
 * @param {string} sortBy
 * @returns {number|null}
 */
export const resolveSharedLotSortPrice = (lot, sortBy) => {
  const prices = collectSharedLotPrices(lot);
  if (!prices.length) return null;
  return sortBy === 'price-asc' ? Math.min(...prices) : Math.max(...prices);
};

/**
 * Superficie del lote catastral (urbana o total).
 * @param {object|null|undefined} lot
 * @returns {number|null}
 */
export const resolveSharedLotSortArea = (lot) => resolveLotAreaM2(lot);

/**
 * @param {object|null|undefined} lot
 * @returns {string}
 */
export const resolveSharedLotSortName = (lot) => (
  String(lot?.lot_name || lot?.name || lot?.lot_id || '').trim()
);

/**
 * Ordena lotes agrupados de lista compartida. Sin número → al final.
 * @param {object[]} lots
 * @param {string} [sortBy]
 * @param {{ getInquiryTotal?: (lot: object) => number }} [options]
 * @returns {object[]}
 */
export function sortSharedGroupedLots(lots, sortBy = DEFAULT_LIST_SORT, options = {}) {
  const list = Array.isArray(lots) ? [...lots] : [];
  const dir = listSortDirection(sortBy);
  const byPrice = sortBy?.startsWith('price');
  const byArea = sortBy?.startsWith('area');
  const byInquiries = isInquirySort(sortBy);
  const getInquiryTotal = options.getInquiryTotal;

  list.sort((a, b) => {
    if (byInquiries) {
      const diff = compareOptionalNumbers(
        Math.max(0, Number(getInquiryTotal?.(a)) || 0),
        Math.max(0, Number(getInquiryTotal?.(b)) || 0),
        dir,
      );
      if (diff !== 0) return diff;
    } else if (byPrice) {
      const diff = compareOptionalNumbers(
        resolveSharedLotSortPrice(a, sortBy),
        resolveSharedLotSortPrice(b, sortBy),
        dir,
      );
      if (diff !== 0) return diff;
    } else if (byArea) {
      const diff = compareOptionalNumbers(
        resolveSharedLotSortArea(a),
        resolveSharedLotSortArea(b),
        dir,
      );
      if (diff !== 0) return diff;
    }
    return compareNames(resolveSharedLotSortName(a), resolveSharedLotSortName(b));
  });
  return list;
}
